import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardCheck, Thermometer } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useCookieConsent from "../hooks/useCookieConsent";
import { CERTIFICATIONS } from "../data/certifications";
import { COOKIES } from "../data/cookies";

const PRACTICES = [
  {
    icon: Thermometer,
    title: "Temperature Logging",
    body: "Every cooler is logged at pack-out and again on delivery — if a shipment ever drifts out of range, it doesn't go out.",
  },
  {
    icon: ClipboardCheck,
    title: "Traceability Records",
    body: "Each order is linked to its source fishery and catch date, retained for full batch traceability in the rare event of a recall.",
  },
];

export default function Compliance() {
  useDocumentTitle("Compliance & Food Safety");
  const { openPreferences } = useCookieConsent();
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[10%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Compliance
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Food Safety, Verified Every Step
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Freshness claims mean nothing without the standards behind them. Here&apos;s what we&apos;re
            certified against and how we hold ourselves to it.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-6xl px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Certifications</h2>
        </ScrollReveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CERTIFICATIONS.map((c, i) => (
            <ScrollReveal key={c.title} delay={i * 0.08}>
              <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500">
                  <c.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-display font-semibold text-ocean-950">{c.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ocean-800/70">{c.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="bg-foam-100 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Day-to-Day Practices</h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {PRACTICES.map((p, i) => (
              <ScrollReveal key={p.title} delay={i * 0.08}>
                <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ocean-900/5 text-ocean-800">
                    <p.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-ocean-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ocean-800/70">{p.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.15} className="mt-8 text-center text-sm text-ocean-800/60">
            Questions about a specific certificate or audit report? Reach out via our{" "}
            <Link to="/contact" className="font-semibold text-teal-600 hover:text-teal-700">
              Contact page
            </Link>
            . Looking for our GSTIN, CIN, or FSSAI licence numbers? See{" "}
            <Link to="/legal" className="font-semibold text-teal-600 hover:text-teal-700">
              Legal &amp; Registrations
            </Link>
            . For our seasonal fishing-ban transparency statement and carbon/community impact, see{" "}
            <Link to="/sustainability" className="font-semibold text-teal-600 hover:text-teal-700">
              Sustainability &amp; Community Impact
            </Link>
            .
          </ScrollReveal>
        </div>
      </section>

      <section id="cookie-policy" className="mx-auto max-w-3xl scroll-mt-24 px-6 py-24">
        <ScrollReveal className="text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Cookie Policy</h2>
          <p className="mt-4 text-ocean-800/70">
            What we store in your browser, why, and for how long.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.08} className="mt-10 overflow-x-auto rounded-2xl ring-1 ring-ocean-900/5">
          <table className="w-full min-w-[480px] text-left text-sm">
            <thead className="bg-foam-100 text-xs uppercase tracking-wide text-ocean-800/60">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Purpose</th>
                <th className="px-4 py-3">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ocean-900/5 bg-white">
              {COOKIES.map((cookie) => (
                <tr key={cookie.name}>
                  <td className="px-4 py-3 font-mono text-xs text-ocean-950">{cookie.name}</td>
                  <td className="px-4 py-3 text-ocean-800/80">{cookie.category}</td>
                  <td className="px-4 py-3 text-ocean-800/70">{cookie.purpose}</td>
                  <td className="px-4 py-3 text-ocean-800/70">{cookie.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollReveal>

        <ScrollReveal delay={0.16} className="mt-6 text-center text-sm text-ocean-800/60">
          We don&apos;t currently set any analytics or marketing cookies — this table will grow if that
          changes.{" "}
          <button
            type="button"
            onClick={openPreferences}
            className="font-semibold text-teal-600 hover:text-teal-700"
          >
            Manage your cookie preferences
          </button>
          .
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
